import { Button, CircularProgress } from "@mui/material";
import { ReactComponent as PolygonSvg } from "assets/polygon.svg";
import Icon from "components/icon/icon";
import Results from "components/results/results";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { DrawShapeSource } from "helpers/models";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import generate from "report/generation";
import getDoc from "report/polygon";
import styles from "./polygon-analysed.module.scss";

function PolygonAnalysed() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const selectedPolygon = context.selectedShapeId
    ? context.shapes.find((pl) => pl.id === context.selectedShapeId)
    : undefined;
  const activeShape = selectedPolygon || (context.currentShape as any);
  const activeShapeId = (activeShape as any)?.id;
  const isPolygonResultOwner =
    !!activeShapeId && context.polygonAnalysisShapeId === activeShapeId;
  const canShowPolygonResult =
    isPolygonResultOwner &&
    (context.polygonAnalysisStatus === "running" ||
      context.polygonAnalysisStatus === "error" ||
      (!!context.polygonAnalysisResult &&
        !!context.polygonAnalysisLegends &&
        !!context.polygonLayerStatistics));

  const handleDownloadPdf = async () => {
    if (
      !context.polygonAnalysisResult ||
      !context.polygonAnalysisLegends ||
      !context.polygonLayerStatistics ||
      context.polygonAnalysisStatus === "running" ||
      context.polygonAnalysisStatus === "error"
    ) {
      return;
    }

    setIsPdfLoading(true);
    try {
      const polygonName = getShapeName();
      const polygonArea = handleArea() || "";
      const fileName =
        polygonName ||
        t("drawShape.polygonInfoTitle", {
          data: (context.shapes.length ?? 0) + 1
        });

      const docDef = await getDoc({
        t,
        polygonName: fileName,
        polygonArea,
        analysisItems: context.polygonAnalysisResult,
        legendItems: context.polygonAnalysisLegends,
        layerStatistics: context.polygonLayerStatistics,
        analysisResult: context.polygonAnalysisResult,
        selectedShapeId: context.selectedShapeId
      });

      await generate(docDef, `${fileName}.pdf`);

      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "reportGenerated",
          message: "setReportPolygonSuccess"
        }
      });
    } catch (e) {
      console.error("PDF generation error:", e);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "downloadPdfTitle",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("snackBar.setReportPolygonFailed")
        }
      });
    } finally {
      setIsPdfLoading(false);
    }
  };

  const getShapeName = () => {
    const activeShape = selectedPolygon || context.currentShape;
    if (!activeShape) return "";
    const cs = activeShape as any;

    if (selectedPolygon?.name) {
      return selectedPolygon.name;
    }

    if (cs.id && cs.coordinates && cs.source !== undefined) {
      return (
        cs.name ||
        t("drawShape.polygonInfoTitle", {
          data: (context.shapes.length ?? 0) + 1
        })
      );
    }

    if (cs.__name) return cs.__name;

    if (context.selectedShapeId) {
      const selectedShape = context.shapes.find(
        (pl) => pl.id === context.selectedShapeId
      );
      if (selectedShape) {
        return selectedShape.name;
      }
    }

    return t("drawShape.polygonInfoTitle", {
      data: (context.shapes.length ?? 0) + 1
    });
  };

  const openModal = (): void => {
    dispatch({ type: AppAction.setAnalyseShapeModal, payload: true });
  };

  const closeRightPanel = (): void => {
    goBack();
  };

  const goBack = () => {
    dispatch({ type: AppAction.setLineAnalysisPanelVisible, payload: false });
    // 清除选中的形状状态
    dispatch({ type: AppAction.deselectShape });
    dispatch({ type: AppAction.setCurrentShape, payload: undefined });
    if (context.currentMultipleLocations) {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
    } else {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    }
  };

  const handleArea = () => {
    const shapeObj =
      (selectedPolygon as any)?.aMapRef ||
      (context.currentShape as any)?.aMapRef ||
      (selectedPolygon as any) ||
      (context.currentShape as any);
    if (!shapeObj?.getPath) {
      return "";
    }

    let path = shapeObj.getPath();
    if (Array.isArray(path?.[0])) {
      path = path[0];
    }

    if (!AMap?.GeometryUtil || path.length < 3) {
      return "";
    }

    const polygonPath = [...path, path[0]];
    const area = Math.abs(AMap.GeometryUtil.ringArea(polygonPath));

    if (area < 1000000) {
      return t("map.area(m2)", { data: area.toFixed(2) });
    }

    return t("map.area(km2)", { data: (area / 1000000).toFixed(3) });
  };

  const isPolygonShape =
    selectedPolygon?.source === DrawShapeSource.POLYGON ||
    selectedPolygon?.source === DrawShapeSource.SHAPE_POLYGON ||
    (context.currentShape as any)?.source === DrawShapeSource.POLYGON ||
    (context.currentShape as any)?.source === DrawShapeSource.SHAPE_POLYGON;

  return (
    <div className={styles.polygonAnalysed}>
      <div className="overflow-auto flex-1">
        {context.currentShape && (
          <div className={styles.title}>
            <div className="flex items-center">
              {context.currentMultipleLocations && (
                <Icon icon="arrow_back" onClick={goBack} />
              )}
              <div className="truncate">{getShapeName()}</div>
            </div>
            <div className="flex items-center">
              <div>
                <Icon onClick={closeRightPanel} />
              </div>
            </div>
          </div>
        )}
        {context.currentShape && isPolygonShape && (
          <div className={styles.titleInfo}>
            <div className={styles.image}>
              <PolygonSvg className="w-[30px] h-[30px]" />
            </div>
            <div className={styles.address}>
              <div className="flex justify-between">
                <div style={{ marginRight: "5px" }}>{t("drawShape.area")}:</div>
                <div style={{ color: "#000000de", fontSize: "13px" }}>
                  {handleArea()}
                </div>
              </div>
            </div>
          </div>
        )}
        {canShowPolygonResult && <Results />}
      </div>
      <div className={styles.buttons}>
        {canShowPolygonResult &&
          context.polygonAnalysisResult &&
          context.polygonAnalysisLegends && (
            <div className="w-fit mr-3">
              <Button
                variant="outlined"
                color="success"
                disabled={isPdfLoading}
                endIcon={
                  isPdfLoading && <CircularProgress color="success" size={24} />
                }
                onClick={handleDownloadPdf}
              >
                {t("buttons.download")}
              </Button>
            </div>
          )}
        <div className="flex-1">
          <Button
            onClick={openModal}
            variant="contained"
            color="success"
            style={{ width: "100%" }}
          >
            {t("buttons.analyseSelected")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PolygonAnalysed;
